// Die Cut Studio's boxes and envelopes as grows: the trees that fold their
// dies (DieCutStudioDieList.ts), every length measured as their builders
// measure it (www.diecutstudio.com, its `head` chunk), so each piece is
// where the die creases it. Their L, W and H are a box's front, side and
// height, our height, depth and width; a tray's base and walls, our width,
// height and depth; an envelope's face, our width and height, its flaps on
// gussets as deep as ours. The slanted creases of a crash-lock bottom are
// no tree's: its flaps turn flat.

import { Max, Sqrt } from "@pdfking/math"
import type { Math } from "@pdfking/quantity"
import { Ast } from "@pdfking/quantity"

import { DieCutStudioBoard, DieCutStudioTongue } from "#internal"
import type { GrowContentMeta, GrowEdge, GrowLayout } from "#public"
import {
  Across,
  Along,
  Content,
  ContentRenderer,
  DIM_DEPTH,
  DIM_HEIGHT,
  DIM_MM,
  DIM_TIMES,
  DIM_WIDTH,
  DIM_ZERO,
  Face,
  Flap,
  FlapPiece,
  Fold,
  GrowContentKind,
  makeGrowEntry,
  NO_PAGES,
  Pages,
  RecordByPrimaryKey,
} from "#public"

const sub = (a: Math, b: Math): Math => Ast.Sub(a, b)
const min = (...values: Math[]): Math => values.reduce((a, b) => Ast.Min(a, b))
const max = (...values: Math[]): Math => values.reduce((a, b) => Ast.Max(a, b))
const clamp = (v: Math, lo: Math, hi: Math): Math => max(lo, min(hi, v))
// Their inches
const inch = (v: number): Math => DIM_MM(v * DieCutStudioBoard.inch)

// The relief cut between two flaps of their board: never under `least`
const relief = (least: number) => DIM_MM(Max(DieCutStudioBoard.thickness, least))

// A panel of the die: a face, lettered as BoxGrowList's are — A the front,
// the page's, B its right, C the back, D its left, E the top, F the bottom; a
// tray A its base, B to E its walls round from the front, F its lid.
const panel = (size: Math, meta: GrowContentMeta = NO_PAGES): GrowLayout =>
  Content(GrowContentKind.PAGE, size, meta)
const FRONT = Pages(1, 2, { label: "A" })
// An envelope's gusset, as deep as the envelope: none for a flat one
const gusset: GrowLayout = Content(GrowContentKind.SPINE, DIM_DEPTH)

// One page over the whole die, labelled a box or an envelope
const die =
  (label: "BOX" | "ENVELOPE") =>
  <I extends string>(id: I, name: string, layout: GrowLayout) =>
    makeGrowEntry(id, name, label, layout, {
      is_box: true,
      is_single_page: true,
      renderer: ContentRenderer.BOX,
      show_cut: false,
      show_crossmark: false,
      // a box's security inside every face; an envelope keeps none
      show_security: label === "BOX",
    })
const box = die("BOX")
const sealed = die("ENVELOPE")

// A box's lengths: L the front, W the side, H the height
const L = DIM_HEIGHT
const W = DIM_DEPTH
const H = DIM_WIDTH

// A straight tuck's tongue
const tongue = DieCutStudioTongue(L, W, {
  min: (a, b) => min(a, b),
  max: (a, b) => max(a, b),
  add: (a, b) => Ast.Add(a, b),
  times: DIM_TIMES,
  inch,
})
const glue_taper = min(DIM_TIMES(H, 0.1), inch(0.2))

// A tuck flap as deep as the side off `edge`, the box's `face`, its tongue
// `depth` deep past it, the tongue's tip narrower by `taper` at either end
const tuck = (edge: GrowEdge, depth: Math, taper: Math, face: GrowContentMeta): GrowLayout =>
  Flap(
    edge,
    Across([
      panel(W, face),
      Flap(edge, FlapPiece(depth), { angle: 90, step: 2, ajar: -0.25, taper: [taper, taper] }),
    ]),
    { angle: 90, step: 1 }
  )

// A dust flap off `edge`, `depth` deep, cut back `chamfer` at its tip's corners
const dust = (edge: GrowEdge, depth: Math, chamfer: Math, step: number): GrowLayout =>
  Flap(edge, FlapPiece(depth), { angle: 90, step, ajar: -0.2, taper: [chamfer, chamfer] })

// The glue tab off the first wall, `depth` deep, its corners cut `taper`
const glue_tab = (depth: Math, taper: Math, step: number): GrowLayout =>
  Flap("left", FlapPiece(depth), { angle: 90, step, ajar: -0.2, taper: [taper, taper] })

// A straight tuck top: the tuck off the back, a dust flap off either side,
// the front's thumb notch — a die's, no tree's
const straight_dust = min(DIM_TIMES(W, 0.85), DIM_TIMES(L, 0.45))
const straight_dust_chamfer = min(DIM_TIMES(straight_dust, 0.5), DIM_TIMES(W, 0.25))
const straight_tongue_taper = min(DIM_TIMES(tongue, 0.7), DIM_TIMES(L, 0.25))

// Straight Tuck End
const bxte = (): GrowLayout =>
  Along(
    [
      panel(L, FRONT),
      tuck("top", tongue, straight_tongue_taper, Face("E")),
      tuck("bottom", tongue, straight_tongue_taper, Face("F")),
      glue_tab(min(inch(0.5), DIM_TIMES(W, 0.9)), glue_taper, 4),
      Fold(90, 4),
      panel(W, Face("B")),
      dust("top", straight_dust, straight_dust_chamfer, 3),
      dust("bottom", straight_dust, straight_dust_chamfer, 3),
      Fold(90, 4),
      panel(L, Face("C")),
      Fold(90, 4),
      panel(W, Face("D")),
      dust("top", straight_dust, straight_dust_chamfer, 3),
      dust("bottom", straight_dust, straight_dust_chamfer, 3),
    ],
    H
  )

// Reverse Tuck End
const bxrt = (): GrowLayout => {
  const tuck_in = clamp(DIM_TIMES(W, 0.5), DIM_MM(8), DIM_TIMES(H, 0.5))
  const round = clamp(min(DIM_TIMES(W, 0.4), DIM_MM(12)), DIM_MM(1.5), DIM_TIMES(tuck_in, 0.45))
  const shoulder = min(
    DIM_TIMES(W, 0.3),
    DIM_MM(8),
    DIM_TIMES(L, 0.25),
    DIM_TIMES(sub(tuck_in, round), 0.9)
  )
  const depth = sub(W, DIM_MM(2))
  const chamfer = min(DIM_TIMES(W, 0.25), DIM_MM(4))
  return Along(
    [
      panel(L, FRONT),
      tuck("top", tuck_in, shoulder, Face("E")),
      glue_tab(
        clamp(DIM_TIMES(W, 0.8), DIM_MM(8), DIM_MM(20)),
        min(DIM_TIMES(H, 0.08), DIM_MM(4)),
        4
      ),
      Fold(90, 4),
      panel(W, Face("B")),
      dust("top", depth, chamfer, 3),
      dust("bottom", depth, chamfer, 3),
      Fold(90, 4),
      panel(L, Face("C")),
      tuck("bottom", tuck_in, shoulder, Face("F", 180)),
      Fold(90, 4),
      panel(W, Face("D")),
      dust("top", depth, chamfer, 3),
      dust("bottom", depth, chamfer, 3),
    ],
    H
  )
}

// A crash-lock bottom's flaps, off the back, a side, the front and the
// other side: the back's and the front's half the side deep, the sides' half
// the front, each in from its walls' relief
const lock_relief = DIM_TIMES(relief(0.8), 0.5)
const major = DIM_TIMES(W, 0.5)
const minor = DIM_TIMES(L, 0.5)
const lock = (
  depth: Math,
  step: number,
  ajar: number,
  from: Math,
  length: Math,
  taper = DIM_ZERO
) =>
  Flap("bottom", FlapPiece(depth), { angle: 90, step, ajar, from, length, taper: [taper, taper] })

// Auto-Bottom Mailer: 2 slanted creases left out
const bxab = (): GrowLayout => {
  const shoulder = min(DIM_TIMES(W, 0.3), DIM_MM(8))
  const depth = sub(W, DIM_MM(2))
  const chamfer = min(DIM_TIMES(W, 0.25), DIM_MM(4))
  const y = lock_relief
  return Along(
    [
      panel(L, FRONT),
      Flap("top", panel(DIM_TIMES(W, 0.85), Face("E")), {
        angle: 90,
        step: 1,
        taper: [shoulder, shoulder],
      }),
      lock(major, 1, 0, DIM_ZERO, sub(L, y)),
      glue_tab(clamp(DIM_TIMES(W, 0.5), DIM_MM(10), DIM_MM(20)), glue_taper, 3),
      Fold(90, 3),
      panel(W, Face("B")),
      dust("top", depth, chamfer, 2),
      lock(minor, 2, -0.2, y, sub(W, DIM_TIMES(y, 2))),
      Fold(90, 3),
      panel(L, Face("C")),
      lock(major, 1, 0, y, sub(L, DIM_TIMES(y, 2))),
      Fold(90, 3),
      panel(W, Face("D")),
      dust("top", depth, chamfer, 2),
      lock(minor, 2, -0.2, y, sub(W, y)),
    ],
    H
  )
}

// Tuck-Top Auto-Bottom: 2 slanted creases left out
const bxtab = (): GrowLayout => {
  const y = lock_relief
  return Along(
    [
      panel(L, FRONT),
      tuck("top", tongue, straight_tongue_taper, Face("E")),
      lock(major, 2, 0, DIM_ZERO, sub(L, y)),
      glue_tab(clamp(DIM_TIMES(W, 0.5), DIM_MM(10), DIM_MM(20)), glue_taper, 4),
      Fold(90, 4),
      panel(W, Face("B")),
      dust("top", straight_dust, straight_dust_chamfer, 3),
      lock(minor, 3, -0.2, y, sub(W, DIM_TIMES(y, 2))),
      Fold(90, 4),
      panel(L, Face("C")),
      lock(major, 2, 0, y, sub(L, DIM_TIMES(y, 2))),
      Fold(90, 4),
      panel(W, Face("D")),
      dust("top", straight_dust, straight_dust_chamfer, 3),
      lock(minor, 3, -0.2, y, sub(W, y)),
    ],
    H
  )
}

// Tuck-Top Snap-Lock: the front's bottom flap locked by a tab off its tip
const bxsl = (): GrowLayout => {
  const y = lock_relief
  const clip = min(DIM_TIMES(minor, 0.35), DIM_TIMES(major, 0.7))
  const tab = clamp(DIM_TIMES(L, 0.4), DIM_MM(18), DIM_TIMES(L, 0.7))
  const tab_depth = min(DIM_TIMES(W, 0.22), DIM_MM(14))
  const side = (from: Math, length: Math, taper: [Math, Math]) =>
    Flap("bottom", FlapPiece(minor), { angle: 90, step: 3, ajar: -0.2, from, length, taper })
  return Along(
    [
      panel(L, FRONT),
      tuck("top", tongue, straight_tongue_taper, Face("E")),
      lock(major, 2, 0, DIM_ZERO, sub(L, y)),
      glue_tab(clamp(DIM_TIMES(W, 0.5), DIM_MM(10), DIM_MM(20)), glue_taper, 4),
      Fold(90, 4),
      panel(W, Face("B")),
      dust("top", straight_dust, straight_dust_chamfer, 3),
      side(y, sub(W, DIM_TIMES(y, 2)), [DIM_ZERO, clip]),
      Fold(90, 4),
      panel(L, Face("C")),
      Flap(
        "bottom",
        Across([
          FlapPiece(major),
          Flap("bottom", FlapPiece(tab_depth), {
            from: sub(DIM_TIMES(sub(L, tab), 0.5), y),
            length: tab,
          }),
        ]),
        { angle: 90, step: 2, from: y, length: sub(L, DIM_TIMES(y, 2)) }
      ),
      Fold(90, 4),
      panel(W, Face("D")),
      dust("top", straight_dust, straight_dust_chamfer, 3),
      side(y, sub(W, y), [clip, DIM_ZERO]),
    ],
    H
  )
}

// Sealed-End Carton
const bxsc = (): GrowLayout => {
  const i = DIM_TIMES(relief(0.8), 0.5)
  const seal = DIM_TIMES(W, 0.5)
  const depth = min(DIM_TIMES(seal, 0.9), sub(DIM_TIMES(L, 0.5), relief(0.8)))
  const chamfer = min(DIM_TIMES(depth, 0.4), DIM_TIMES(W, 0.2))
  const sealed = (from: Math, length: Math) =>
    (["top", "bottom"] as const).map(edge =>
      Flap(edge, FlapPiece(seal), { angle: 90, step: 1, from, length })
    )
  const dusted = (from: Math, length: Math) =>
    (["top", "bottom"] as const).map(edge =>
      Flap(edge, FlapPiece(depth), {
        angle: 90,
        step: 2,
        ajar: -0.2,
        from,
        length,
        taper: [chamfer, chamfer],
      })
    )
  return Along(
    [
      FlapPiece(clamp(DIM_TIMES(W, 0.6), DIM_MM(8), DIM_MM(16))),
      Fold(90, 3, -0.2),
      panel(L, FRONT),
      ...sealed(DIM_ZERO, sub(L, i)),
      Fold(90, 3),
      panel(W, Face("B")),
      ...dusted(i, sub(W, DIM_TIMES(i, 2))),
      Fold(90, 3),
      panel(L, Face("C")),
      ...sealed(i, sub(L, DIM_TIMES(i, 2))),
      Fold(90, 3),
      panel(W, Face("D")),
      ...dusted(i, sub(W, i)),
    ],
    H
  )
}

// A tray's lengths: L its base's width, W its height, H how deep its walls
// stand
const TL = DIM_WIDTH
const TW = DIM_HEIGHT
const TH = DIM_DEPTH
const tray_relief = relief(1)
const tray_flap = clamp(DIM_TIMES(TH, 0.8), DIM_MM(5), sub(DIM_TIMES(TL, 0.5), tray_relief))
const tray_chamfer = min(DIM_TIMES(tray_flap, 0.4), DIM_MM(6))

// An end wall's flaps, glued inside the side walls, cut back at their outer
// corner: the left end's at its start, the right end's at its end
const end_flaps = (left: boolean, step: number): GrowLayout[] =>
  (["top", "bottom"] as const).map(edge =>
    Flap(edge, FlapPiece(tray_flap), {
      angle: 90,
      step,
      ajar: -0.2,
      from: left ? tray_relief : DIM_ZERO,
      length: sub(TH, tray_relief),
      taper: left ? [tray_chamfer, DIM_ZERO] : [DIM_ZERO, tray_chamfer],
    })
  )

// Open Tray
const bxot = (): GrowLayout =>
  Along(
    [
      panel(TH, Face("E", 90)),
      ...end_flaps(true, 2),
      Fold(90, 3),
      panel(TL, FRONT),
      Flap("top", panel(TH, Face("B")), { angle: 90, step: 1 }),
      Flap("bottom", panel(TH, Face("D", 180)), { angle: 90, step: 1 }),
      Fold(90, 3),
      panel(TH, Face("C", 270)),
      ...end_flaps(false, 2),
    ],
    TW
  )

// Hinged-Lid Tray: the back wall goes on into the lid, the lid into its tuck
const bxht = (): GrowLayout => {
  const tuck_depth = clamp(DIM_TIMES(TH, 0.7), DIM_MM(8), sub(TH, tray_relief))
  const tuck_chamfer = min(DIM_TIMES(tuck_depth, 0.5), DIM_TIMES(TL, 0.1))
  return Along(
    [
      panel(TH, Face("C", 90)),
      ...end_flaps(true, 3),
      Fold(90, 4),
      panel(TL, FRONT),
      Flap(
        "top",
        Across([
          panel(TH, Face("D")),
          Flap(
            "top",
            Across([
              panel(TW, Face("F", 180)),
              Flap("top", FlapPiece(tuck_depth), {
                angle: 90,
                step: 3,
                ajar: -0.25,
                taper: [tuck_chamfer, tuck_chamfer],
              }),
            ]),
            { angle: 90, step: 2 }
          ),
        ]),
        { angle: 90, step: 1 }
      ),
      Flap("bottom", panel(TH, Face("B", 180)), { angle: 90, step: 2 }),
      Fold(90, 4),
      panel(TH, Face("E", 270)),
      ...end_flaps(false, 3),
    ],
    TW
  )
}

// Regular Slotted Container (RSC): four flaps either end, half the side
// deep, slotted between
const bxrsc = (): GrowLayout => {
  const l = DIM_TIMES(relief(3), 0.5)
  const flaps = (step: number, ajar: number, from: Math, length: Math) =>
    (["top", "bottom"] as const).map(edge =>
      Flap(edge, FlapPiece(DIM_TIMES(W, 0.5)), { angle: 90, step, ajar, from, length })
    )
  return Along(
    [
      FlapPiece(clamp(DIM_TIMES(min(L, W), 0.25), DIM_MM(28), DIM_MM(45))),
      Fold(90, 3, -0.2),
      panel(L, FRONT),
      ...flaps(1, 0, DIM_ZERO, sub(L, l)),
      Fold(90, 3),
      panel(W, Face("B")),
      ...flaps(2, -0.2, l, sub(W, DIM_TIMES(l, 2))),
      Fold(90, 3),
      panel(L, Face("C")),
      ...flaps(1, 0, l, sub(L, DIM_TIMES(l, 2))),
      Fold(90, 3),
      panel(W, Face("D")),
      ...flaps(2, -0.2, l, sub(W, l)),
    ],
    H
  )
}

// Gable Box: a roof off the front and the back, turned 45 degrees to meet,
// an ear off either end of each tucked under; a gable off either side, never
// turned; a tuck bottom off the front
const bxgb = (): GrowLayout => {
  const gable = DIM_TIMES(W, 0.5)
  // The roof's slope, from the eaves to the ridge over the side's middle
  const roof = DIM_TIMES(W, Sqrt(0.5))
  const ear = min(DIM_MM(11), DIM_TIMES(roof, 0.32))
  const ear_width = clamp(DIM_TIMES(L, 0.13), DIM_MM(10), DIM_TIMES(L, 0.24))
  const ear_cut = min(DIM_TIMES(ear_width, 0.35), DIM_TIMES(ear, 0.55))
  const ears = (from: Math, taper: [Math, Math]): GrowLayout =>
    Flap("top", FlapPiece(ear), { angle: -45, step: 2, from, length: ear_width, taper })
  const roofed = Flap(
    "top",
    Across([
      FlapPiece(roof),
      ears(DIM_ZERO, [ear_cut, DIM_ZERO]),
      ears(sub(L, ear_width), [DIM_ZERO, ear_cut]),
    ]),
    { angle: 45, step: 1 }
  )
  const gabled = Flap("top", FlapPiece(gable), { taper: [gable, gable] })
  const bottom_tongue = min(DIM_TIMES(W, 0.5), inch(0.7))
  const dust_depth = min(DIM_TIMES(W, 0.85), DIM_TIMES(L, 0.45))
  const dusted = dust("bottom", dust_depth, min(DIM_TIMES(dust_depth, 0.5), DIM_TIMES(W, 0.25)), 2)
  return Along(
    [
      panel(L, Face("C")),
      roofed,
      tuck(
        "bottom",
        bottom_tongue,
        min(DIM_TIMES(bottom_tongue, 0.7), DIM_TIMES(L, 0.25)),
        Face("F", 180)
      ),
      glue_tab(min(inch(0.5), DIM_TIMES(W, 0.9)), glue_taper, 3),
      Fold(90, 3),
      panel(W, Face("D")),
      gabled,
      dusted,
      Fold(90, 3),
      panel(L, FRONT),
      roofed,
      Fold(90, 3),
      panel(W, Face("B")),
      gabled,
      dusted,
    ],
    H
  )
}

// An envelope's flap off `edge`, on its gusset, `depth` deep, its tip
// narrower by `taper` at either end; along the edge `from` its start for
// `length`, the whole edge when unsaid
const envelope_flap = (
  edge: GrowEdge,
  step: number,
  depth: Math,
  taper: [Math, Math],
  from: Math = DIM_ZERO,
  length: Math | undefined = undefined
): GrowLayout => {
  const parts = [gusset, Fold(90, step), FlapPiece(depth)]
  return Flap(edge, edge === "top" || edge === "bottom" ? Across(parts) : Along(parts), {
    angle: 90,
    step,
    from,
    length,
    taper,
  })
}

// An envelope's lengths: L its face's width, W its height
const EL = DIM_WIDTH
const EW = DIM_HEIGHT

// A wallet or catalog envelope: each flap its share of the face deep, inset
// at its tip
const envelope = (top: number, side: number, bottom: number): GrowLayout => {
  const r = DIM_TIMES(EW, top)
  const l = DIM_TIMES(EL, side)
  const u = DIM_TIMES(EW, bottom)
  const i = clamp(DIM_MM(5), DIM_MM(0), DIM_TIMES(min(l, r, u), 0.4))
  return Along(
    [
      panel(EL, FRONT),
      envelope_flap("top", 1, r, [i, i]),
      envelope_flap("bottom", 2, u, [i, i]),
      envelope_flap("left", 3, l, [i, i]),
      envelope_flap("right", 3, l, [i, i]),
    ],
    EW
  )
}

// Envelope (diamond): every flap pointed but the bottom one, the face's
// corners cut back between them
const envd = (): GrowLayout => {
  const side = DIM_TIMES(EL, 90 / 229)
  const above = DIM_TIMES(EW, 135 / 162)
  const below = DIM_TIMES(EW, 90 / 162)
  const point = DIM_TIMES(EL, 70 / 229)
  const corner = clamp(DIM_MM(3), DIM_MM(0), DIM_TIMES(min(side, above, below), 0.25))
  const across = sub(EL, DIM_TIMES(corner, 2))
  const down = sub(EW, DIM_TIMES(corner, 2))
  const to_point = DIM_TIMES(across, 0.5)
  const to_tip = DIM_TIMES(sub(across, point), 0.5)
  const to_side = DIM_TIMES(down, 0.5)
  return Along(
    [
      panel(EL, FRONT),
      envelope_flap("top", 1, above, [to_point, to_point], corner, across),
      envelope_flap("bottom", 2, below, [to_tip, to_tip], corner, across),
      envelope_flap("left", 3, side, [to_side, to_side], corner, down),
      envelope_flap("right", 3, side, [to_side, to_side], corner, down),
    ],
    EW
  )
}

// Envelope (pointed): the top flap pointed, the rest cut in at their tips
const envb = (): GrowLayout => {
  const side = clamp(DIM_TIMES(EL, 0.32), DIM_MM(10), DIM_TIMES(EL, 0.46))
  const above = clamp(DIM_TIMES(EW, 0.52), DIM_MM(20), DIM_TIMES(EW, 0.85))
  const below = clamp(DIM_TIMES(EW, 0.5), DIM_MM(15), DIM_TIMES(EW, 0.7))
  const side_taper = clamp(side, DIM_MM(3), sub(DIM_TIMES(EW, 0.5), DIM_MM(3)))
  const bottom_taper = clamp(DIM_TIMES(side, 0.6), DIM_MM(3), sub(DIM_TIMES(EL, 0.5), DIM_MM(8)))
  const point = DIM_TIMES(EL, 0.5)
  return Along(
    [
      panel(EL, FRONT),
      envelope_flap("top", 1, above, [point, point]),
      envelope_flap("bottom", 2, below, [bottom_taper, bottom_taper]),
      envelope_flap("left", 3, side, [side_taper, side_taper]),
      envelope_flap("right", 3, side, [side_taper, side_taper]),
    ],
    EW
  )
}

export const DieCutStudioGrowList = RecordByPrimaryKey([
  box("bxte", "GROW_KIND_BXTE", bxte()),
  box("bxrt", "GROW_KIND_BXRT", bxrt()),
  box("bxab", "GROW_KIND_BXAB", bxab()),
  box("bxtab", "GROW_KIND_BXTAB", bxtab()),
  box("bxsl", "GROW_KIND_BXSL", bxsl()),
  box("bxsc", "GROW_KIND_BXSC", bxsc()),
  box("bxot", "GROW_KIND_BXOT", bxot()),
  box("bxht", "GROW_KIND_BXHT", bxht()),
  box("bxrsc", "GROW_KIND_BXRSC", bxrsc()),
  box("bxgb", "GROW_KIND_BXGB", bxgb()),
] as const)

// Die Cut Studio's envelopes, a list of their own.
export const EnvelopeGrowList = RecordByPrimaryKey([
  sealed("envp", "GROW_KIND_ENVP", envelope(50 / 229, 20 / 162, 190 / 229)),
  sealed("envd", "GROW_KIND_ENVD", envd()),
  sealed("envc", "GROW_KIND_ENVC", envelope(50 / 229, 85 / 162, 35 / 229)),
  sealed("envb", "GROW_KIND_ENVB", envb()),
] as const)
